import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Lock, Eye, EyeOff, Shield, CheckCircle2, Trash2, AlertTriangle } from 'lucide-react';
import { schoolInfo } from '../utils/mock';
import { toast } from 'sonner';
import axios from 'axios';
import { emergencyClearCache } from '../utils/cacheBuster';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  
  // Attendance Settings
  const [attendanceSettings, setAttendanceSettings] = useState({
    total_working_days: 210
  });
  const [savingAttendance, setSavingAttendance] = useState(false);

  useEffect(() => {
    // Verify admin authentication
    const verifyAdmin = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        await axios.get(`${BACKEND_URL}/api/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Fetch attendance settings
        const attendanceResponse = await axios.get(`${BACKEND_URL}/api/attendance-settings`);
        setAttendanceSettings(attendanceResponse.data);
        
        setLoading(false);
      } catch (err) {
        console.error('Verification failed:', err);
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUsername');
        navigate('/admin/login');
      }
    };

    verifyAdmin();
  }, [navigate]);

  const validateForm = () => {
    const newErrors = {};

    // Check if all fields are filled
    if (!passwordForm.current_password) {
      newErrors.current_password = 'Current password is required';
    }

    if (!passwordForm.new_password) {
      newErrors.new_password = 'New password is required';
    } else if (passwordForm.new_password.length < 6) {
      newErrors.new_password = 'Password must be at least 6 characters long';
    }

    if (!passwordForm.confirm_password) {
      newErrors.confirm_password = 'Please confirm your new password';
    } else if (passwordForm.new_password !== passwordForm.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    // Check if new password is same as current
    if (passwordForm.current_password && passwordForm.new_password && 
        passwordForm.current_password === passwordForm.new_password) {
      newErrors.new_password = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
    // Clear error for this field
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setUpdating(true);
    const token = localStorage.getItem('adminToken');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(true);
      toast.success('Password updated successfully!', {
        description: 'Your admin password has been changed securely'
      });

      // Reset form
      setTimeout(() => {
        setPasswordForm({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        setSuccess(false);
      }, 3000);

    } catch (err) {
      console.error('Password change error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to update password';
      
      if (errorMessage.includes('Current password is incorrect')) {
        setErrors({ current_password: 'Current password is incorrect' });
      }
      
      toast.error('Password update failed', {
        description: errorMessage
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleAttendanceChange = (e) => {
    setAttendanceSettings({
      ...attendanceSettings,
      total_working_days: parseInt(e.target.value) || 210
    });
  };

  const handleAttendanceSubmit = async (e) => {
    e.preventDefault();
    
    if (attendanceSettings.total_working_days < 1 || attendanceSettings.total_working_days > 365) {
      toast.error('Invalid value', {
        description: 'Total working days must be between 1 and 365'
      });
      return;
    }
    
    setSavingAttendance(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/attendance-settings`,
        attendanceSettings
      );

      toast.success('Attendance settings updated!', {
        description: `Total working days set to ${attendanceSettings.total_working_days}`
      });
    } catch (err) {
      console.error('Error updating attendance settings:', err);
      toast.error('Failed to update settings', {
        description: err.response?.data?.detail || 'Please try again'
      });
    } finally {
      setSavingAttendance(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-700 font-medium">Loading Settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={schoolInfo.logo} 
                alt="AMTEE PUBLIC SCHOOL" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Settings</h1>
                <p className="text-sm text-gray-600">Manage your admin account</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/dashboard')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Attendance Settings Card */}
          <Card className="shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-lg">
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Attendance Settings
              </CardTitle>
              <CardDescription className="text-green-100">
                Configure total working days for attendance calculations
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-8">
              <Alert className="bg-green-50 border-green-200 mb-6">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <AlertDescription className="text-green-800">
                  This value is used system-wide for calculating attendance percentages.
                </AlertDescription>
              </Alert>

              <form onSubmit={handleAttendanceSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="total_working_days" className="text-base font-semibold">
                    Total Working Days per Year *
                  </Label>
                  <Input
                    id="total_working_days"
                    name="total_working_days"
                    type="number"
                    min="1"
                    max="365"
                    value={attendanceSettings.total_working_days}
                    onChange={handleAttendanceChange}
                    className="mt-2 py-6 text-lg"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Default: 210 days (excluding weekends and holidays)
                  </p>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">
                    📊 How It's Used:
                  </h4>
                  <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                    <li>Attendance Percentage = (Present Days ÷ Total Working Days) × 100</li>
                    <li>Used in teacher attendance marking</li>
                    <li>Displayed on parent dashboard</li>
                    <li>Used in attendance reports</li>
                  </ul>
                </div>

                {/* Preview Calculation */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border-2 border-purple-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">📈 Example Calculation:</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>If a student attended <strong>180 days</strong> out of <strong>{attendanceSettings.total_working_days} days</strong>:</p>
                    <p className="text-lg font-bold text-purple-600">
                      Percentage = {((180 / attendanceSettings.total_working_days) * 100).toFixed(2)}%
                    </p>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg font-semibold"
                  disabled={savingAttendance}
                >
                  {savingAttendance ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-5 w-5" />
                      Save Attendance Settings
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Security Settings Card */}
          <Card className="shadow-xl border-0">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Security Settings
            </CardTitle>
            <CardDescription className="text-blue-100">
              Change your admin password to keep your account secure
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-8">
            {success && (
              <Alert className="bg-green-50 border-green-200 mb-6 animate-fade-in">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <AlertDescription className="text-green-800 font-medium">
                  Password updated successfully! Your account is now more secure.
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Current Password */}
              <div>
                <Label htmlFor="current_password" className="text-base font-semibold">
                  Current Password *
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="current_password"
                    name="current_password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordForm.current_password}
                    onChange={handleChange}
                    placeholder="Enter your current password"
                    className={`pl-10 pr-10 py-6 ${errors.current_password ? 'border-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.current_password && (
                  <p className="text-red-600 text-sm mt-1">{errors.current_password}</p>
                )}
              </div>

              {/* New Password */}
              <div>
                <Label htmlFor="new_password" className="text-base font-semibold">
                  New Password *
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="new_password"
                    name="new_password"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordForm.new_password}
                    onChange={handleChange}
                    placeholder="Enter new password (min 6 characters)"
                    className={`pl-10 pr-10 py-6 ${errors.new_password ? 'border-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.new_password && (
                  <p className="text-red-600 text-sm mt-1">{errors.new_password}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Password must be at least 6 characters long
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirm_password" className="text-base font-semibold">
                  Confirm New Password *
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordForm.confirm_password}
                    onChange={handleChange}
                    placeholder="Re-enter your new password"
                    className={`pl-10 pr-10 py-6 ${errors.confirm_password ? 'border-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.confirm_password && (
                  <p className="text-red-600 text-sm mt-1">{errors.confirm_password}</p>
                )}
              </div>

              {/* Security Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                  🔐 Password Security Tips
                </h4>
                <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                  <li>Use at least 6 characters (longer is better)</li>
                  <li>Mix uppercase, lowercase, numbers, and symbols</li>
                  <li>Avoid common words or personal information</li>
                  <li>Don't reuse passwords from other accounts</li>
                </ul>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setPasswordForm({
                      current_password: '',
                      new_password: '',
                      confirm_password: ''
                    });
                    setErrors({});
                    setSuccess(false);
                  }}
                  className="flex-1"
                  disabled={updating}
                >
                  Reset Form
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 py-6 text-lg font-semibold"
                  disabled={updating}
                >
                  {updating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-5 w-5" />
                      Update Password
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

          {/* PWA Cache Management Card */}
          <Card className="shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-t-lg">
              <CardTitle className="text-2xl font-bold flex items-center gap-2">
                <Trash2 className="h-6 w-6" />
                Cache Management
              </CardTitle>
              <CardDescription className="text-red-100">
                Clear app cache to fix display issues or force latest version
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-8">
              <Alert className="bg-yellow-50 border-yellow-200 mb-6">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  <strong>Warning:</strong> This will clear all cached data and reload the app. Use this if users are seeing old versions.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">🔄 What This Does:</h4>
                  <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
                    <li>Clears all service worker caches</li>
                    <li>Removes locally stored data</li>
                    <li>Forces app to fetch latest version</li>
                    <li>Automatically reloads the page</li>
                  </ul>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">📱 When to Use:</h4>
                  <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                    <li>Users seeing old UI after updates</li>
                    <li>Features not appearing as expected</li>
                    <li>After deploying new changes</li>
                    <li>Troubleshooting display issues</li>
                  </ul>
                </div>

                <Button
                  onClick={() => {
                    if (window.confirm('This will clear all caches and reload the app. Continue?')) {
                      toast.info('Clearing caches...');
                      emergencyClearCache();
                    }
                  }}
                  className="w-full bg-red-600 hover:bg-red-700 py-6 text-lg font-semibold"
                >
                  <Trash2 className="mr-2 h-5 w-5" />
                  Clear All Caches & Reload
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
